import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { getCodeBySystem } from "@medplum/core";
import { Observation } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Menu } from "@mantine/core";

import {
  LABOR_AMEND_FLOWSHEET_SAVE_ERROR_STAGE_MOVE_MESSAGE,
  LABOR_AMEND_FLOWSHEET_SAVE_ERROR_TITLE,
  LABOR_AMEND_QUEUED_MESSAGE,
} from "@/encounters/components/EncounterLabor/constants/labor-encounter-amend";
import { useEnqueueLaborAmendPersist } from "@/encounters/components/EncounterLabor/hooks/use-enqueue-labor-amend-persist";
import {
  fetchLaborStageObservations,
  getOrCreateLaborStage,
  updateObservationMembership,
} from "@/encounters/components/EncounterLabor/services/labor";
import { LaborStage, LaborStages } from "@/encounters/components/EncounterLabor/types/labor";
import { fetchLaborStagesAfterMembershipMove } from "@/encounters/components/EncounterLabor/utils/fetch-labor-stages-after-membership-move";
import {
  buildLaborStageMovePendingDisplayDetails,
  getLaborFlowsheetRowDisplayName,
} from "@/encounters/components/EncounterLabor/utils/labor-amend-preview";
import { dedupeFinalLaborStageObservations } from "@/encounters/components/EncounterLabor/utils/labor-stage-dedupe";
import { getLaborFlowsheetObservationDisplayName } from "@/encounters/components/EncounterLabor/utils/labor-stage-version-diff";
import { useLaborAmendFlowsheetPersist } from "@/encounters/contexts/LaborAmendFlowsheetPersistContext";
import { LaborAmendMembershipWriteBlockedError } from "@/encounters/utils/labor-amend-membership-write-guard";
import { Systems } from "@/fhir/enums/systems";
import { RootState } from "@/store";
import { loadLaborStages } from "@/store/slices/labor-stages";
import { joinReferenceString } from "@/utils/medplum";

interface EditLaborStageProps {
  currentObservation: Observation;
  onLaborStageChange?: () => void;
}

export default function EditLaborStage({
  currentObservation,
  onLaborStageChange,
}: EditLaborStageProps) {
  const medplum = useMedplum();
  const dispatch = useDispatch();
  const enqueueAmendPersist = useEnqueueLaborAmendPersist();
  const {
    deferFlowsheetPersists,
    canExecuteDeferredFlowsheetPersist,
    getLaborStageObservationsForFlush,
    registerAmendProvenanceTarget,
    registerAmendStageMembershipChange,
  } = useLaborAmendFlowsheetPersist();
  const birthObservationId = useSelector((state: RootState) => state.birth.observation?.id);
  const { clientId, encounterId } = useParams();
  const [currentLaborStage, setCurrentLaborStage] = useState<Observation | undefined>(undefined);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    const abortController = new AbortController();
    let isMounted = true;

    const fetchData = async () => {
      if (!clientId || !encounterId) return;

      try {
        const observations = await fetchLaborStageObservations(medplum, clientId, encounterId);

        if (isMounted) {
          const parentObservation = observations.find((obs) =>
            obs.hasMember?.some(
              (member) => member.reference === `Observation/${currentObservation.id}`,
            ),
          );

          setCurrentLaborStage(parentObservation);
        }
      } catch (error) {
        if (isMounted) {
          console.error("Error fetching labor stage observations:", error);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [medplum, clientId, encounterId, currentObservation.id]);

  const handleLaborStageSelect = async (stageKey: string) => {
    if (!clientId || !encounterId) {
      console.error("Missing clientId or encounterId");
      return;
    }

    try {
      setIsUpdating(true);

      const persistLaborStageMoveToMedplum = async () => {
        if (deferFlowsheetPersists && !canExecuteDeferredFlowsheetPersist()) {
          throw new LaborAmendMembershipWriteBlockedError();
        }

        const memberObservationId = currentObservation.id;
        if (!memberObservationId) {
          throw new Error("Cannot move labor stage row without an observation id.");
        }

        const stagesSnapshot = await getLaborStageObservationsForFlush(
          medplum,
          clientId,
          encounterId,
          fetchLaborStageObservations,
        );
        const laborStageSnapshot = stagesSnapshot.find((stage) =>
          stage.hasMember?.some(
            (member) => member.reference === `Observation/${memberObservationId}`,
          ),
        );

        const targetStage = await getOrCreateLaborStage(
          medplum,
          stagesSnapshot,
          stageKey,
          clientId,
          encounterId,
        );

        await updateObservationMembership(
          medplum,
          memberObservationId,
          laborStageSnapshot,
          targetStage,
        );

        registerAmendProvenanceTarget(currentObservation);
        if (laborStageSnapshot) {
          registerAmendProvenanceTarget(laborStageSnapshot);
        }
        registerAmendProvenanceTarget(targetStage);

        const fromStageLoincCode = laborStageSnapshot?.valueCodeableConcept
          ? getCodeBySystem(laborStageSnapshot.valueCodeableConcept, Systems.LOINC)
          : undefined;
        registerAmendStageMembershipChange({
          memberObservationId,
          memberDisplayTitle: getLaborFlowsheetObservationDisplayName(
            currentObservation.code?.coding?.[0]?.code,
          ),
          fromStageLoincCode: fromStageLoincCode ?? undefined,
          toStageLoincCode: stageKey,
          fromStageDisplay: fromStageLoincCode
            ? LaborStages[fromStageLoincCode as LaborStage]
            : undefined,
          toStageDisplay: LaborStages[stageKey as LaborStage],
        });

        const observations = await fetchLaborStagesAfterMembershipMove(
          medplum,
          clientId,
          encounterId,
          targetStage.id,
          memberObservationId,
        );

        dispatch(loadLaborStages(dedupeFinalLaborStageObservations(observations)));

        for (const observation of observations) {
          registerAmendProvenanceTarget(observation);
        }
        if (birthObservationId) {
          const birthDateTimeObservation = observations.find(
            (obs) => obs.id === birthObservationId,
          );
          if (birthDateTimeObservation) {
            registerAmendProvenanceTarget(birthDateTimeObservation);
          } else {
            registerAmendProvenanceTarget({
              reference: joinReferenceString("Observation", birthObservationId),
            });
          }
        }

        const newParentObservation = observations.find((obs) =>
          obs.hasMember?.some(
            (member) => member.reference === `Observation/${memberObservationId}`,
          ),
        );
        setCurrentLaborStage(newParentObservation);

        onLaborStageChange?.();
      };

      const replaceKey = `labor-stage-move:${currentObservation.id}`;
      const targetStageLabel = LaborStages[stageKey as keyof typeof LaborStages] ?? stageKey;
      const fromStageKey = currentLaborStage?.valueCodeableConcept
        ? getCodeBySystem(currentLaborStage.valueCodeableConcept, Systems.LOINC)
        : undefined;
      const fromStageLabel = fromStageKey
        ? (LaborStages[fromStageKey as keyof typeof LaborStages] ?? fromStageKey)
        : undefined;

      const wasAmendPersistEnqueued = enqueueAmendPersist({
        replaceKey,
        pendingDisplayEntry: {
          key: replaceKey,
          entryType: "stage-move",
          title: getLaborFlowsheetRowDisplayName(currentObservation),
          details: buildLaborStageMovePendingDisplayDetails(currentObservation, {
            fromStageLabel,
            targetStageLabel,
          }),
        },
        persist: persistLaborStageMoveToMedplum,
        queuedMessage: LABOR_AMEND_QUEUED_MESSAGE,
        errorTitle: LABOR_AMEND_FLOWSHEET_SAVE_ERROR_TITLE,
        errorMessage: LABOR_AMEND_FLOWSHEET_SAVE_ERROR_STAGE_MOVE_MESSAGE,
      });
      if (wasAmendPersistEnqueued) {
        return;
      }

      await persistLaborStageMoveToMedplum();
    } catch (error) {
      console.error("Error updating labor stage:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const currentStageKey = currentLaborStage?.valueCodeableConcept
    ? getCodeBySystem(currentLaborStage.valueCodeableConcept, Systems.LOINC)
    : undefined;

  return (
    <Menu.Dropdown>
      {Object.entries(LaborStages)
        .filter(([key]) => key !== currentStageKey)
        .map(([key, value]) => (
          <Menu.Item key={key} disabled={isUpdating} onClick={() => handleLaborStageSelect(key)}>
            {value}
          </Menu.Item>
        ))}
    </Menu.Dropdown>
  );
}
